import { useState, useEffect, useCallback, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { ethers } from 'ethers'
import config from '../config.json'
import { getSwapQuote, executeSwap } from '../store/interactions'

import ethIcon from '../assets/eth.svg'
import usdtIcon from '../assets/usdt.svg'
import paxgIcon from '../assets/paxg.svg'

const TOKEN_LIST = [
  { symbol: 'ETH', icon: ethIcon, decimals: 18 },
  { symbol: 'USDT', icon: usdtIcon, decimals: 6 },
  { symbol: 'PAXG', icon: paxgIcon, decimals: 18 }
]

const SwapPage = () => {
  const dispatch = useDispatch()
  const provider = useSelector(state => state.provider.connection)
  const chainId = useSelector(state => state.provider.chainId)
  const account = useSelector(state => state.provider.account)
  const swapState = useSelector(state => state.swap)

  const [tokenIn, setTokenIn] = useState('ETH')
  const [tokenOut, setTokenOut] = useState('USDT')
  const [amountIn, setAmountIn] = useState('')
  const [amountOut, setAmountOut] = useState('')
  const [slippage, setSlippage] = useState('0.5')
  const [isQuoting, setIsQuoting] = useState(false)
  const [quoteError, setQuoteError] = useState('')
  const [balanceIn, setBalanceIn] = useState('0')
  const [priceRoute, setPriceRoute] = useState(null)

  const debounceRef = useRef(null)

  const getTokenAddress = useCallback((symbol) => {
    if (!chainId || !config[chainId]) return null
    if (symbol === 'ETH') return ethers.constants.AddressZero
    return config[chainId][symbol]?.address || null
  }, [chainId])

  const getDecimals = (symbol) => {
    const t = TOKEN_LIST.find(tk => tk.symbol === symbol)
    return t ? t.decimals : 18
  }

  // Load balance for tokenIn
  useEffect(() => {
    const loadBalance = async () => {
      if (!provider || !account) return
      try {
        const addr = getTokenAddress(tokenIn)
        if (addr === null) return
        if (addr === ethers.constants.AddressZero) {
          const bal = await provider.getBalance(account)
          setBalanceIn(ethers.utils.formatEther(bal))
        } else {
          const erc20 = new ethers.Contract(addr, [
            'function balanceOf(address) view returns (uint256)',
            'function decimals() view returns (uint8)'
          ], provider)
          const decimals = await erc20.decimals()
          const bal = await erc20.balanceOf(account)
          setBalanceIn(ethers.utils.formatUnits(bal, decimals))
        }
      } catch (e) {
        console.error('Balance load error:', e)
        setBalanceIn('0')
      }
    }
    loadBalance()
  }, [provider, account, tokenIn, chainId, getTokenAddress, swapState])

  // Fetch quote via Paraswap API
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)

    if (!amountIn || parseFloat(amountIn) <= 0 || tokenIn === tokenOut || chainId !== 1) {
      setAmountOut('')
      setQuoteError('')
      setPriceRoute(null)
      return
    }

    debounceRef.current = setTimeout(async () => {
      setIsQuoting(true)
      setQuoteError('')
      setPriceRoute(null)
      try {
        const addrIn = getTokenAddress(tokenIn)
        const addrOut = getTokenAddress(tokenOut)
        if (!addrIn || !addrOut) {
          setQuoteError('Token not configured for this network')
          setAmountOut('')
          setIsQuoting(false)
          return
        }

        const decimalsIn = getDecimals(tokenIn)
        const decimalsOut = getDecimals(tokenOut)
        const parsedIn = ethers.utils.parseUnits(amountIn, decimalsIn)

        const route = await getSwapQuote(addrIn, addrOut, parsedIn, decimalsIn, decimalsOut, chainId)
        setAmountOut(ethers.utils.formatUnits(route.destAmount, decimalsOut))
        setPriceRoute(route)
      } catch (e) {
        console.error('Quote error:', e)
        setAmountOut('')
        setQuoteError(e.message || 'No liquidity for this pair')
      }
      setIsQuoting(false)
    }, 500)

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [amountIn, tokenIn, tokenOut, chainId, getTokenAddress])

  const handleFlip = () => {
    setTokenIn(tokenOut)
    setTokenOut(tokenIn)
    setAmountIn('')
    setAmountOut('')
    setQuoteError('')
    setPriceRoute(null)
    dispatch({ type: 'SWAP_RESET' })
  }

  const handleMaxClick = () => {
    setAmountIn(balanceIn)
  }

  const handleSwap = async () => {
    if (!provider || !account || !priceRoute) return

    try {
      await executeSwap(provider, priceRoute, account, parseFloat(slippage), dispatch)
      setAmountIn('')
      setAmountOut('')
      setPriceRoute(null)
    } catch (error) {
      console.error('Swap error:', error)
      dispatch({ type: 'SWAP_FAIL', error: error.message })
    }
  }

  const getExchangeRate = () => {
    if (!amountIn || !amountOut || parseFloat(amountIn) <= 0) return null
    const rate = parseFloat(amountOut) / parseFloat(amountIn)
    return `1 ${tokenIn} = ${rate.toFixed(6)} ${tokenOut}`
  }

  const getTokenIcon = (symbol) => {
    const t = TOKEN_LIST.find(tk => tk.symbol === symbol)
    return t ? t.icon : null
  }

  const isMainnet = chainId === 1

  return (
    <div className={`swap-container${!isMainnet ? ' swap-container--disabled' : ''}`}>
      <div className="vault-header">
        <svg className="vault-emblem" width="60" height="60" viewBox="0 0 60 60" fill="none" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <linearGradient id="swapGold" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#DAA520" />
              <stop offset="50%" stopColor="#FFD700" />
              <stop offset="100%" stopColor="#B8960C" />
            </linearGradient>
          </defs>
          <circle cx="30" cy="30" r="27" stroke="url(#swapGold)" strokeWidth="2" fill="none" />
          <path d="M22 24L30 16L38 24" stroke="url(#swapGold)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M38 36L30 44L22 36" stroke="url(#swapGold)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
          <line x1="30" y1="16" x2="30" y2="36" stroke="url(#swapGold)" strokeWidth="2" strokeLinecap="round" />
          <line x1="30" y1="24" x2="30" y2="44" stroke="url(#swapGold)" strokeWidth="2" strokeLinecap="round" />
        </svg>
        <h2 className="vault-title">Swap</h2>
        <span className="vault-subtitle">Instant Token Exchange</span>
      </div>

      {!isMainnet && (
        <div className="swap-mainnet-notice">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <span>Available on Ethereum Mainnet only</span>
        </div>
      )}

      {/* Token In */}
      <div className="swap-input-group">
        <label className="vault-form__label">You Pay</label>
        <div className="swap-token-select">
          <div className="swap-token-select__left">
            <img src={getTokenIcon(tokenIn)} alt={tokenIn} className="swap-token-select__icon" />
            <select
              value={tokenIn}
              onChange={(e) => {
                if (e.target.value === tokenOut) setTokenOut(tokenIn)
                setTokenIn(e.target.value)
                setAmountIn('')
                setAmountOut('')
                setPriceRoute(null)
              }}
              className="swap-token-select__dropdown"
            >
              {TOKEN_LIST.map(t => (
                <option key={t.symbol} value={t.symbol}>{t.symbol}</option>
              ))}
            </select>
          </div>
          <input
            type="number"
            className="swap-token-select__input"
            placeholder="0.0"
            value={amountIn}
            onChange={(e) => {
              setAmountIn(e.target.value)
              if (swapState.isError || swapState.isSuccessful) dispatch({ type: 'SWAP_RESET' })
            }}
            step="any"
            min="0"
          />
        </div>
        <div className="swap-balance">
          <span>Balance: {Number(balanceIn).toFixed(6)}</span>
          {priceRoute?.srcUSD && amountIn && <span className="swap-usd">~${Number(priceRoute.srcUSD).toFixed(2)}</span>}
          <button className="swap-balance__max" onClick={handleMaxClick} type="button">MAX</button>
        </div>
      </div>

      {/* Flip Button */}
      <div className="swap-flip-wrapper">
        <button className="swap-flip-btn" onClick={handleFlip} type="button">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M7 16V4M7 4L3 8M7 4L11 8" />
            <path d="M17 8V20M17 20L21 16M17 20L13 16" />
          </svg>
        </button>
      </div>

      {/* Token Out */}
      <div className="swap-input-group">
        <label className="vault-form__label">You Receive</label>
        <div className="swap-token-select">
          <div className="swap-token-select__left">
            <img src={getTokenIcon(tokenOut)} alt={tokenOut} className="swap-token-select__icon" />
            <select
              value={tokenOut}
              onChange={(e) => {
                if (e.target.value === tokenIn) setTokenIn(tokenOut)
                setTokenOut(e.target.value)
                setAmountOut('')
                setPriceRoute(null)
              }}
              className="swap-token-select__dropdown"
            >
              {TOKEN_LIST.map(t => (
                <option key={t.symbol} value={t.symbol}>{t.symbol}</option>
              ))}
            </select>
          </div>
          <div className="swap-output">
            {isQuoting ? 'Loading...' : (amountOut ? Number(amountOut).toFixed(6) : '0.0')}
          </div>
        </div>
        {priceRoute?.destUSD && amountOut && (
          <div className="swap-balance">
            <span className="swap-usd">~${Number(priceRoute.destUSD).toFixed(2)}</span>
          </div>
        )}
      </div>

      {/* Quote Error */}
      {quoteError && (
        <div className="swap-error">{quoteError}</div>
      )}

      {/* Details */}
      {amountOut && !quoteError && (
        <div className="swap-details">
          <div className="swap-details__row">
            <span>Rate</span>
            <strong>{getExchangeRate()}</strong>
          </div>
          <div className="swap-details__row">
            <span>Slippage Tolerance</span>
            <div className="swap-details__slippage">
              <input
                type="number"
                className="swap-details__slippage-input"
                value={slippage}
                onChange={(e) => setSlippage(e.target.value)}
                step="0.1"
                min="0.1"
                max="50"
              />
              <span>%</span>
            </div>
          </div>
          <div className="swap-details__row">
            <span>Minimum Received</span>
            <strong>
              {(parseFloat(amountOut) * (1 - parseFloat(slippage) / 100)).toFixed(6)} {tokenOut}
            </strong>
          </div>
        </div>
      )}

      {/* Swap Button */}
      <button
        className="vault-form__submit"
        onClick={handleSwap}
        disabled={
          !isMainnet ||
          !account ||
          !amountIn ||
          !amountOut ||
          parseFloat(amountIn) <= 0 ||
          swapState.isPending ||
          isQuoting ||
          !!quoteError ||
          !priceRoute
        }
      >
        {!isMainnet
          ? 'Mainnet Only'
          : !account
            ? 'Connect Wallet'
            : swapState.isPending
              ? 'Swapping...'
              : tokenIn === tokenOut
                ? 'Select Different Tokens'
                : !amountIn || parseFloat(amountIn) <= 0
                  ? 'Enter Amount'
                  : quoteError
                    ? 'No Liquidity'
                    : `Swap ${tokenIn} for ${tokenOut}`
        }
      </button>

      {/* Status Feedback */}
      {swapState.isSuccessful && swapState.txHash && (
        <div className="swap-status swap-status--success">
          Swap successful!
          {chainId && config[chainId]?.explorerURL && (
            <a
              href={`${config[chainId].explorerURL}tx/${swapState.txHash}`}
              target="_blank"
              rel="noreferrer"
            >
              View on Explorer
            </a>
          )}
        </div>
      )}

      {swapState.isError && (
        <div className="swap-status swap-status--error">
          {swapState.errorMessage || 'Swap failed. Please try again.'}
        </div>
      )}
    </div>
  )
}

export default SwapPage
